import { AssetsApi } from '../../../../api/assetsApi';
import { MappingApi } from '../../../../api/mappingApi';
import { TemplatesApi } from '../../../../api/templatesApi';
import { BatchDeterminationContext } from '../../../../contexts/BatchDeterminationContext';
import { FileContext } from '../../../../contexts/FileContext';
import { AdvancedOptions, MappingInfo, TargetField } from '../../../../interfaces/Mapping';
import { SnackbarParameters } from '../../../../interfaces/Snackbar';
import { Template } from '../../../../interfaces/Templates';
import Loader from '../../../../shared/components/Loader';
import withSnackbar from '../../../../shared/components/withSnackbar';
import {
	DEFAULT_TEMPLATE_INFO,
	IMPORT_PROCESS_STATE,
	PREVIEW_FILE_ROWS,
	TEMPLATE_COMPATIBILITY_ERROR_MESSAGE,
} from '../../../../shared/constants/ImportPageConstants';
import { CUSTOM_FORMAT, CUSTOM_IMPORT } from '../../../../shared/constants/constants';
import { isFileIsCompatible } from '../../../../shared/helpers/importProcessSlidingPanelHelpers';
import CreateTemplateDialog from './Dialogs/CreateTemplateDialog';
import RenameTemplateDialog from './Dialogs/RenameTemplateDialog';
import UpdateGlobalTemplateDialog from './Dialogs/UpdateGlobalTemplateDialog';
import UpdateTemplateDialog from './Dialogs/UpdateTemplateDialog';
import AdvancedOptionsComponent from './advancedOptions/AdvancedOptions';
import ManageMappingColumnCards from './columnsMapping/ManageMappingColumnCards';
import {
	SovosButton,
	SovosDivider,
	SovosFormControl,
	SovosIconMenu,
	SovosMenuItem,
	SovosTextField,
	SovosTooltip,
	SovosTypography,
} from 'mosaic-react';
import { AddCircle, Help, MoreVert } from 'mosaic-react-icons';
import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';

const TARGET_FIELDS = 'targetFields';

interface MappingStepPageProps {
	processStatus: number;
	mappedColumns: MappingInfo[];
	allTemplatesFetched: Template[];
	selectedCustomTemplate: Template;
	advancedOptions: AdvancedOptions;
	setMappedColumns: (value: MappingInfo[]) => void;
	setAllTemplatesFetched: Dispatch<SetStateAction<Template[]>>;
	setSelectedCustomTemplate: Dispatch<SetStateAction<Template>>;
	setAdvancedOptions: Dispatch<SetStateAction<AdvancedOptions>>;
	selectedGlobalTemplate: Template;
	columnCardsToHide: MappingInfo[];
	globalMappingData: MappingInfo[];
	onDisableBeginProcessingButton: (value: boolean) => void;
	onFetchingTargets: (value: boolean) => void;
	onPreviewFail: (value: boolean) => void;
	columnsHeaders: MappingInfo[];
	setColumnsHeaders: Dispatch<SetStateAction<MappingInfo[]>>;
	showSnackbar: (value: SnackbarParameters) => void;
	isFetchingTargets: boolean;
	handleMatchingStepClick: () => void;
	isDisableBeginProcessingButton: boolean;
	haveExtraRows: boolean;
	handleOnPrevious: () => void;
	setCheckboxSelected: Dispatch<SetStateAction<boolean>>;
}

const MappingStepPage = (props: MappingStepPageProps) => {
	const {
		mappedColumns,
		allTemplatesFetched,
		selectedCustomTemplate,
		advancedOptions,
		setMappedColumns,
		setAllTemplatesFetched,
		setSelectedCustomTemplate,
		setAdvancedOptions,
		showSnackbar,
		selectedGlobalTemplate,
		columnCardsToHide,
		globalMappingData,
		onDisableBeginProcessingButton,
		onFetchingTargets,
		onPreviewFail,
		processStatus,
		columnsHeaders,
		setColumnsHeaders,
		isFetchingTargets,
		handleMatchingStepClick,
		isDisableBeginProcessingButton,
		haveExtraRows,
		handleOnPrevious,
		setCheckboxSelected,
	} = props;
	const customTemplateInfoSelected = allTemplatesFetched.find(template => template.id === selectedCustomTemplate.id);

	const [isMounted, setIsMounted] = useState<boolean>(false);
	const [isDisableActionsTemplate, seIsDisableActionsTemplate] = useState<boolean>(true);
	const [mappedColumnsCount, setMappedColumnsCount] = useState<number>(
		mappedColumns.filter(column => column.targetField !== '' && column.targetField !== null).length
	);
	const storedPreviewFileRows = sessionStorage.getItem(PREVIEW_FILE_ROWS);
	const storedTargetFields = sessionStorage.getItem(TARGET_FIELDS);
	const [previewFileRows, setPreviewFileRows] = useState<any[]>(
		storedPreviewFileRows !== null ? [...JSON.parse(storedPreviewFileRows)] : []
	);
	const [targetFields, setTargetFields] = useState<TargetField[]>(
		storedTargetFields !== null ? [...JSON.parse(storedTargetFields)] : []
	);
	const [skippedColumnsCount, setSkippedColumnsCount] = useState<number>(
		mappedColumns.filter(column => column.targetField === '').length
	);
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState<boolean>(false);
	const [isRenameDialogOpen, setIsRenameDialogOpen] = useState<boolean>(false);
	const [forceReloadingTemplateId, setForceReloadingTemplateId] = useState<number>(0);
	const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState<boolean>(false);
	const [isUpdateGlobalTemplateDialogOpen, setIsUpdateGlobalTemplateDialogOpen] = useState<boolean>(false);
	const [isDisableUpdateGlobalTemplateButton, setIsDisableUpdateGlobalTemplateButton] = useState<boolean>(true);
	const [templateMappingInfo, setTemplateMappingInfo] = useState<MappingInfo[]>(
		selectedCustomTemplate.id === '' && selectedGlobalTemplate.id !== CUSTOM_FORMAT ? globalMappingData : []
	);
	const [isDisabledApplyButton, setIsDisabledApplyButton] = useState<boolean>(false);
	const [selectedAdvancedOptions, setSelectedAdvancedOptions] = useState({});
	const { forceRefreshingTemplateTable, onClose } = useContext(BatchDeterminationContext);
	const { selectedFiles } = useContext(FileContext);
	const fileId = selectedFiles[0]?.id;

	const sleep = (ms: number): Promise<void> => {
		return new Promise(resolve => setTimeout(resolve, ms));
	};

	const getPreviewFileRows = (): void => {
		let ruleType = undefined;

		if (selectedGlobalTemplate.name !== CUSTOM_IMPORT) {
			ruleType = selectedGlobalTemplate.ruleType;
		}

		AssetsApi.getPreviewRows(fileId, ruleType)
			.then(response => {
				if (response.status === 400 && response.data.message === 'Unable to get the asset header and row') {
					onPreviewFail(true);
					showSnackbar({
						message: 'Make sure that your file has the proper content before trying to process',
						variant: 'warning',
					});
					sleep(5000).then(() => {
						onClose();
					});
				} else {
					setPreviewFileRows(response.data.rows);
					sessionStorage.setItem(PREVIEW_FILE_ROWS, JSON.stringify(response.data.rows));
					onPreviewFail(false);
				}
			})
			.catch(error => {
				console.log('Error fetching preview rows');
			});
	};

	const getPremapInfo = (): Promise<void> =>
		AssetsApi.getPreMapInfo(fileId)
			.then(response => {
				setMappedColumns(response);
				updateSkippedColumns(response);
			})
			.catch(error => {
			console.log('Error fetching premap info');
		});

	const getTargetFields = (): void => {
		onFetchingTargets(true);
		MappingApi.getTargetFields(advancedOptions.calculationType)
			.then(response => {
				const targetFieldsWithRequiredAndDisabled = response.map(targetField => ({
					...targetField,
					isRequired:
						targetField.name === 'sTGeoCd' ||
						targetField.name === 'sTLocCd' ||
						targetField.name === 'sTStateProv' ||
						targetField.name === 'sTPstlCd' ||
						targetField.isRequired,
					disabled: mappedColumns.some(column => column.targetField === targetField.name),
				}));
				setTargetFields(targetFieldsWithRequiredAndDisabled);
				sessionStorage.setItem(TARGET_FIELDS, JSON.stringify(targetFieldsWithRequiredAndDisabled));
				handleDisableBeginProcessingButton(targetFieldsWithRequiredAndDisabled);
				onFetchingTargets(false);
			})
			.catch(error => {
				console.log('Error fetching target fields');
			});
	};

	const getImportTemplate = useCallback(
		() =>
			TemplatesApi.getAllTemplates()
				.then(response => {
					const allTemplate = response.map(template => ({
						...template,
						thousandSeparator: template.thousandSeparator ?? '',
					}));
				setAllTemplatesFetched(allTemplate);
				})
						.catch(error => {
					console.log('Error fetching import templates');
				}),
				[setAllTemplatesFetched]
	);

	const getTemplateAdvancedOptions = useCallback(
		() =>
			TemplatesApi.getTemplate(selectedCustomTemplate.id)
				.then(response => {
					const { calculationType, dateFormat, transactionType } = response;
				setSelectedAdvancedOptions({ calculationType, dateFormat, transactionType });
				})
						.catch(error => {
					console.log('Error fetching template advanced options');
				}),
				[selectedCustomTemplate.id]
	);

	const getMappingTemplateInfo = useCallback(
		(globalTemplate: Template | undefined) => {
			const selectedTemplate = allTemplatesFetched.find(template => template.id === selectedCustomTemplate.id);
			let mappingId;
			if (selectedTemplate !== undefined) mappingId = selectedTemplate?.mapping?.id;
			if (selectedTemplate === undefined && globalTemplate !== undefined) mappingId = globalTemplate?.mapping?.id;
			setIsDisabledApplyButton(true);
			return MappingApi.getMappingTemplateInfo(mappingId)
				.then(response => {
					const mappingData = response;
					setTemplateMappingInfo(mappingData);
					setIsDisabledApplyButton(false);
				})
			.catch(error => {
				console.log('Error fetching mapping template info');
			});
		},
		[allTemplatesFetched, selectedCustomTemplate.id]
	);

	useEffect(() => {
		const isCustomTemplate = selectedCustomTemplate && selectedCustomTemplate.id !== '';
		if (isCustomTemplate) {
			getMappingTemplateInfo(undefined);
			getTemplateAdvancedOptions();
		}
		if (
			selectedGlobalTemplate.id !== CUSTOM_FORMAT &&
			!isCustomTemplate &&
			processStatus !== IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT
		) {
			const totalColumnsMapped = mappedColumns.filter(
				column => column.targetField !== '' && column.targetField !== null
			).length;
			setSkippedColumnsCount(mappedColumns.length - totalColumnsMapped);
			setTemplateMappingInfo(globalMappingData);
		}
	}, [
		selectedCustomTemplate,
		getTemplateAdvancedOptions,
		selectedGlobalTemplate,
		mappedColumns.length,
		processStatus,
		columnCardsToHide.length,
		getMappingTemplateInfo,
		globalMappingData,
		mappedColumns,
	]);

	useEffect(() => {
		if (isMounted) getTargetFields();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [advancedOptions.calculationType]);

	useEffect(() => {
		if ((mappedColumnsCount === 0 || selectedGlobalTemplate.id !== CUSTOM_FORMAT) && !isMounted) {
			getTargetFields();
			getPreviewFileRows();
			setIsMounted(true);
			if (selectedGlobalTemplate.id === CUSTOM_FORMAT) getPremapInfo();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (processStatus === IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT && mappedColumns.length > 0) {
			let newMappedColumnsCount = mappedColumns.filter(
				column => column.sourceField !== '' && column.sourceField !== null
			).length;
			let skippedColumns = columnsHeaders.length - newMappedColumnsCount;
			setSkippedColumnsCount(skippedColumns);
			setMappedColumnsCount(newMappedColumnsCount);
			handleDisableUpdateGlobalTemplateButton(targetFields);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mappedColumns, processStatus]);

	useEffect(() => {
		getImportTemplate();
	}, [getImportTemplate, forceReloadingTemplateId]);

	const handleCreateDialogClose = (template: Template | undefined): void => {
		if (template && template.id) {
			getImportTemplate().then(() => setSelectedCustomTemplate(template));
		}
		setIsCreateDialogOpen(false);
	};

	const forceReloadingTemplate = (): void => {
		setForceReloadingTemplateId(forceReloadingTemplateId + 1);
		forceRefreshingTemplateTable();
	};

	const handleClickApplyButton = (): void => {
		if (isFileIsCompatible(mappedColumns, templateMappingInfo)) {
			setAdvancedOptions({ ...advancedOptions, ...selectedAdvancedOptions });
			cleanMappedColumns();
			templateMappingInfo.forEach(mapping => {
				handleColumnMapping(mapping.targetField, mapping.sourceField);
			});
		} else {
			showSnackbar({ message: TEMPLATE_COMPATIBILITY_ERROR_MESSAGE, variant: 'error' });
		}
		setMappedColumns([...mappedColumns]);
	};

	const cleanMappedColumns = (): void => {
		mappedColumns.forEach(column => (column.targetField = ''));
	};

	const updateTargetsFields = (): void => {
		const newTargetFields = targetFields.map(targetField => ({
			...targetField,
			disabled: mappedColumns.some(column => column.targetField === targetField.name),
		}));
		setTargetFields(newTargetFields);
		sessionStorage.setItem(TARGET_FIELDS, JSON.stringify(newTargetFields));

		if (processStatus === IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT) {
			handleDisableUpdateGlobalTemplateButton(newTargetFields);
		} else {
			const newMappedColumnsCount = mappedColumns.filter(
				column => column.targetField !== '' && column.targetField !== null
			).length;
			setMappedColumnsCount(newMappedColumnsCount);
			handleDisableBeginProcessingButton(newTargetFields);
		}
	};

	const handleColumnMapping = (mappedField: string, mappedColumnName: string): void => {
		const mappedColumn = mappedColumns.find(
			column => column.sourceField.toUpperCase() === mappedColumnName.toUpperCase()
		);
		if (mappedColumn) {
			mappedColumn.targetField = mappedField;
			updateTargetsFields();
			if (processStatus !== IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT) {
				updateSkippedColumns(mappedColumns);
			}
		}
	};

	const handleTargetMapping = (columnId: number, targetField: string): void => {
		const column = mappedColumns.find(column => column.id === columnId);
		if (column) {
			column.targetField = targetField;
			setMappedColumns([...mappedColumns]);
			updateTargetsFields();
			if (processStatus !== IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT) {
				updateSkippedColumns(mappedColumns);
			}
		}
	};

	const getColumnHeader = (sourceField: string, targetField: string, disabled: boolean, id: number): MappingInfo => ({
		id,
		sourceField,
		targetField,
		disabled,
	});

	const handleHeaderMapping = (columnId: number, sourceField: string): void => {
		const column = mappedColumns.find(column => column.id === columnId);
		if (column) {
			const oldSourceFieldMapped = column.sourceField;
			column.sourceField = sourceField;
			setMappedColumns([...mappedColumns]);

			const newColumnsHeaders = columnsHeaders.map(columnHeader => {
				if (columnHeader.sourceField === sourceField && columnHeader.targetField === '') {
					return getColumnHeader(columnHeader.sourceField, column.targetField, true, columnHeader.id);
				} else if (columnHeader.sourceField === oldSourceFieldMapped) {
					return getColumnHeader(columnHeader.sourceField, '', false, columnHeader.id);
				} else {
					return getColumnHeader(
						columnHeader.sourceField,
						columnHeader.targetField,
						mappedColumns.some(column => column.sourceField === columnHeader.sourceField),
						columnHeader.id
					);
				}
			});

			setColumnsHeaders([...newColumnsHeaders]);
			handleDisableUpdateGlobalTemplateButton([...newColumnsHeaders]);
		}
	};

	const handleClickApplyAddColumnButton = (): void => {
		if (mappedColumns[0].sourceField !== '') {
			mappedColumns.unshift({ sourceField: '', targetField: '', id: 0 });
			setMappedColumns([...mappedColumns]);
			updateTargetsFields();
		}
	};

	const handleSelectTemplate = (templateId: string): void => {
		if (templateId) setSelectedCustomTemplate(allTemplatesFetched.find(template => template.id === templateId)!);
	};

	const isRequiredFields = (targetFields: TargetField[]): boolean => {
		const isSTGeoCode = !mappedColumns.some(column => column.targetField === 'sTGeoCd');
		const isSTLocCd = !mappedColumns.some(column => column.targetField === 'sTLocCd');
		const isStateProvAndPstlCd =
			!mappedColumns.some(column => column.targetField === 'sTStateProv') ||
			!mappedColumns.some(column => column.targetField === 'sTPstlCd');

		return (
			!isFieldsMapped(getRequiredFieldsWithOutGeoSatePostal(targetFields)) ||
			(isSTGeoCode && isSTLocCd && isStateProvAndPstlCd)
		);
	};

	const handleDisableBeginProcessingButton = (targetFields: TargetField[]): void => {
		if (processStatus !== IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT) {
			if (isRequiredFields(targetFields) && selectedGlobalTemplate.id === CUSTOM_FORMAT) {
				onDisableBeginProcessingButton(true);
				seIsDisableActionsTemplate(true);
			} else {
				onDisableBeginProcessingButton(false);
				seIsDisableActionsTemplate(false);
			}
		}
	};

	const handleDisableUpdateGlobalTemplateButton = (targetFields: any[]): void => {
		processStatus === IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT && !isRequiredFields(targetFields)
			? mappedColumns.some(
					column => (column.targetField === '' && column.sourceField === '') || column.sourceField === ''
			  )
				? setIsDisableUpdateGlobalTemplateButton(true)
				: setIsDisableUpdateGlobalTemplateButton(false)
			: setIsDisableUpdateGlobalTemplateButton(true);
	};

	const isFieldsMapped = (requiredFields: string[]): boolean => {
		let response = !requiredFields.some(
			requiredField => !mappedColumns.some(column => column.targetField === requiredField)
		);
		return response;
	};

	const getRequiredFieldsWithOutGeoSatePostal = (targetFields: TargetField[]) => {
		let requiredFields = [];
		for (const index in targetFields) {
			if (
				targetFields[index].isRequired &&
				targetFields[index].name !== 'sTGeoCd' &&
				targetFields[index].name !== 'sTLocCd' &&
				targetFields[index].name !== 'sTStateProv' &&
				targetFields[index].name !== 'sTPstlCd'
			) {
				requiredFields.push(targetFields[index].name);
			}
		}
		return requiredFields;
	};

	const deleteColumnCard = (columnId: number, sourceField: string): void => {
		if (mappedColumns.length > 1) {
			const newColumnsMapped = mappedColumns.filter(column => column.id !== columnId);
			setMappedColumns([...newColumnsMapped]);
			updateTargetsFields();
			if (sourceField !== '') {
				const newHeaderFields = columnsHeaders.map(columnHeader =>
					columnHeader.sourceField === sourceField
						? { sourceField: columnHeader.sourceField, targetField: '', disable: false, id: columnHeader.id }
						: { ...columnHeader }
				);
				setColumnsHeaders([...newHeaderFields]);
			}
			handleDisableUpdateGlobalTemplateButton(targetFields);
		}
	};

	const updateSkippedColumns = (columns: MappingInfo[]): void => {
		const newSkippedColumnsCount = columns.filter(column => column.targetField === '').length;
		setSkippedColumnsCount(newSkippedColumnsCount);
	};

	const renderToolTip = (): JSX.Element => (
		<div style={{ marginLeft: 'auto', fontWeight: 'bold' }}>
			<SovosTooltip title='For this template, if Ship to Geocode or Ship to Location Code is mapped, Ship to State/Province and Ship to Postal Code fields are not required, and vice versa.'>
				<Help />
			</SovosTooltip>
		</div>
	);

	const renderContent = (processStatus: number): JSX.Element => {
		switch (processStatus) {
			case IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT:
				return (
					<>
						<SovosTypography variant='h6' style={{ fontWeight: 500 }}>
							{selectedGlobalTemplate.name}
						</SovosTypography>
						<br />
						<SovosTypography variant='body1'>You are editing the {selectedGlobalTemplate.name}.</SovosTypography>
						<br />
						<div style={{ display: 'flex', alignItems: 'flex-end' }}>
							<SovosButton style={{ fontWeight: 'bold' }} onClick={() => handleClickApplyAddColumnButton()}>
								ADD COLUMN
							</SovosButton>
							{renderToolTip()}
						</div>
						<br />
						<br />
					</>
				);
			default:
				return (
					<>
						<SovosTypography variant='h6' style={{ fontWeight: 500 }}>
							Match the headers of your file to the Sovos fields.
						</SovosTypography>

						<div style={{ display: 'flex', alignItems: 'center', marginTop: 20 }}>
							<div style={{ width: 300 }}>
								<SovosFormControl style={{ width: '100%' }}>
									<SovosTextField
										variant='outlined'
										label='Import Template (optional)'
										value={selectedCustomTemplate.id}
										onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleSelectTemplate(event.target.value)}
										select
									>
										<SovosMenuItem
											value=''
											onClick={() => setIsCreateDialogOpen(true)}
											style={{ background: 'unset', paddingBottom: '15px' }}
											disabled={isDisableActionsTemplate}
										>
											<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
												<AddCircle />
												<span>Save New Template...</span>
											</div>
										</SovosMenuItem>
										<SovosDivider />
										{allTemplatesFetched.map(template => (
											<SovosMenuItem value={template.id} key={template.id}>
												<span>{template.name}</span>
											</SovosMenuItem>
										))}
									</SovosTextField>
								</SovosFormControl>
							</div>
							{selectedCustomTemplate && selectedCustomTemplate.id && (
								<div style={{ alignItems: 'center' }}>
									<SovosButton
										color='primary'
										style={{ marginLeft: 20, fontWeight: 'bold' }}
										onClick={() => handleClickApplyButton()}
										disabled={isDisabledApplyButton}
									>
										Apply
									</SovosButton>
									<SovosIconMenu
										iconElement={<MoreVert />}
										toolTipText='All required fields must be mapped before templates can be created or updated'
									>
										<SovosMenuItem value={1} onClick={() => setIsRenameDialogOpen(true)}>
											Rename
										</SovosMenuItem>
										<SovosMenuItem
											value={2}
											onClick={() => setIsUpdateDialogOpen(true)}
											disabled={isDisableActionsTemplate}
										>
											Update
										</SovosMenuItem>
									</SovosIconMenu>
								</div>
							)}
							{renderToolTip()}
						</div>
						<br />
					</>
				);
		}
	};

	const areAllColumnsMapped = Boolean(mappedColumnsCount === mappedColumns.length - skippedColumnsCount);

	const completedFetching =
		IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT === processStatus
			? Boolean(mappedColumns.length !== 0 && targetFields.length !== 0)
			: Boolean(mappedColumns.length !== 0 && previewFileRows.length !== 0 && targetFields.length !== 0);

	return (
		<>
			{renderContent(processStatus)}

			{completedFetching ? (
				<>
					<>
						<ManageMappingColumnCards
							targetFields={targetFields}
							mappedColumns={mappedColumns}
							previewFileRows={previewFileRows}
							onColumnMapped={handleTargetMapping}
							columnCardsToHide={columnCardsToHide}
							onDeleteColumnCards={(columnId: number, sourceField: string) => deleteColumnCard(columnId, sourceField)}
							onHeaderColumnMapped={handleHeaderMapping}
							processStatus={processStatus}
							columnsHeaders={columnsHeaders}
						/>
						<br />
						<SovosTypography
							variant='body1'
							style={{ fontWeight: 500, color: mappedColumns.length === mappedColumnsCount ? 'green' : '' }}
						>
							{mappedColumnsCount} out {mappedColumns.length} headers matched
							{areAllColumnsMapped && mappedColumnsCount > 0 ? '!' : ''}
						</SovosTypography>
						<SovosTypography variant='body2' style={{ marginTop: 5 }}>
							{skippedColumnsCount === 0 ? 'No' : skippedColumnsCount} headers will be skipped
						</SovosTypography>
					</>

					<>
						{(selectedGlobalTemplate.id === CUSTOM_FORMAT ||
							processStatus === IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT ||
							processStatus === IMPORT_PROCESS_STATE.MATCHING) && (
							<div>
								<SovosDivider style={{ marginTop: 30, marginBottom: 20 }} />

								{(selectedGlobalTemplate.id === CUSTOM_FORMAT || processStatus !== IMPORT_PROCESS_STATE.MATCHING) && (
									<>
										<AdvancedOptionsComponent
											advancedOptions={advancedOptions}
											setAdvancedOptions={setAdvancedOptions}
										/>
										<SovosDivider style={{ marginTop: 30, marginBottom: 20 }} />
									</>
								)}

								<div style={{ display: 'flex', justifyContent: 'center' }}>
									<SovosButton
										variant='text'
										size='large'
										onClick={() => {
											setPreviewFileRows([]);
											setCheckboxSelected(false);
											handleOnPrevious();
										}}
									>
										Previous
									</SovosButton>
									{processStatus !== IMPORT_PROCESS_STATE.GLOBAL_TEMPLATE_EDIT ? (
										<SovosButton
											onClick={!isFetchingTargets ? handleMatchingStepClick : () => null}
											size='large'
											disabled={isDisableBeginProcessingButton || haveExtraRows}
										>
											Next
										</SovosButton>
									) : (
										<SovosButton
											onClick={!isFetchingTargets ? () => setIsUpdateGlobalTemplateDialogOpen(true) : () => null}
											disabled={isDisableUpdateGlobalTemplateButton}
										>
											Save
										</SovosButton>
									)}
								</div>
							</div>
						)}
					</>
				</>
			) : (
				<div style={{ textAlign: 'center', padding: 40 }}>
					<Loader />
				</div>
			)}
			<CreateTemplateDialog
				isDialogOpen={isCreateDialogOpen}
				onCloseDialog={() => setIsCreateDialogOpen(false)}
				onClose={handleCreateDialogClose}
				mappedColumns={mappedColumns}
				templateInfo={{
					...DEFAULT_TEMPLATE_INFO,
					...advancedOptions,
				}}
				selectedGlobalTemplate={selectedGlobalTemplate}
				forceReloadingTemplate={forceReloadingTemplate}
				showSnackbar={() => null}
			/>
			<RenameTemplateDialog
				isDialogOpen={isRenameDialogOpen}
				closeDialog={() => setIsRenameDialogOpen(false)}
				template={customTemplateInfoSelected!}
				forceReloadingTemplate={forceReloadingTemplate}
				showSnackbar={() => null}
			/>

			<UpdateTemplateDialog
				isDialogOpen={isUpdateDialogOpen}
				onClose={() => setIsUpdateDialogOpen(false)}
				mappedColumns={mappedColumns}
				templateInfo={{
					...customTemplateInfoSelected,
					...advancedOptions,
				}}
				ruleType={selectedGlobalTemplate.ruleType!}
				forceReloadingTemplate={forceReloadingTemplate}
				allTemplatesFetched={allTemplatesFetched}
				showSnackbar={() => null}
			/>
			<UpdateGlobalTemplateDialog
				isDialogOpen={isUpdateGlobalTemplateDialogOpen}
				mappedColumns={mappedColumns}
				selectedTemplate={selectedGlobalTemplate}
				advancedOptions={advancedOptions}
				onCloseDialog={() => setIsUpdateGlobalTemplateDialogOpen(false)}
				showSnackbar={() => null}
			/>
		</>
	);
};

export default withSnackbar(MappingStepPage);
